import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useChatSearch } from '@/hooks/useChatSearch';
import { useDashboard } from '@/hooks/useDashboard';
import {
    AgentConversation,
    agentService,
    EVENT_TYPES
} from '@common/api/agentService';
import { ContextItem } from '@common/components/AgentInput';
import { Icon } from '@common/index';
import React, {
    useCallback, useEffect, useRef, useState
} from 'react';
import { useLocation } from 'react-router-dom';
import ChatSidebarUI from './ChatSidebarUI';

interface ChatSidebarProps {
    className?: string;
    onClose?: () => void;
    mode?: 'chat' | 'dashboard';
    isEditMode?: boolean;
    onToggleEditMode?: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
    className, 
    onClose, 
    mode = 'chat',
    isEditMode,
}) => {
    const location = useLocation();
    const { profile, user } = useAuth();
    
    // Chat Context
    const {
        chat: {
            getCurrentMessages,
            isLoadingConversation,
            setIsLoadingConversation,
            setCurrentConversationId,
            isGenerating,
            messages,
            setMessages,
            history,
            setHistory,
            isLoadingHistory,
            setIsLoadingHistory,
            context,
            setContext
        },
        dashboard
    } = useAdminContext();

    const currentMessages = getCurrentMessages();
    const {
        handleSearch, chatSideBarScrollRef, cancelCurrentRequest
    } = useChatSearch();

    // Dashboard Hooks
    const { generateDashboard, dashboardScrollRef } = useDashboard();

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [showToBottom, setShowToBottom] = useState(false);

    const isDashboardMode = mode === 'dashboard';

    // Data Source Selection
    const useDashboardState = isDashboardMode && isEditMode;
    
    const activeMessages = useDashboardState ? dashboard.messages : currentMessages;
    const activeHistory = useDashboardState ? dashboard.conversations : history;
    const activeIsLoadingConversation = useDashboardState ? dashboard.isLoadingMessages : isLoadingConversation;
    const activeIsLoadingHistory = useDashboardState ? dashboard.isLoadingConversations : isLoadingHistory;
    const activeIsGenerating = useDashboardState ? dashboard.isGenerating : isGenerating;
    const activeScrollRef = useDashboardState ? dashboardScrollRef : chatSideBarScrollRef;

    // Chat Actions
    const fetchConversationHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const conversations = await agentService.getConversations();
            setHistory(conversations);
        } catch (error) {
            console.error('Failed to fetch conversation history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [setIsLoadingHistory, setHistory]);

    // 初始化时加载对话历史（仅在非 dashboard 模式下）
    useEffect(() => {
        if (!useDashboardState) {
            fetchConversationHistory();
        }
    }, [useDashboardState, fetchConversationHistory]);

    const handleHistoryItemClick = async (historyItem: AgentConversation) => {
        setCurrentConversationId(historyItem.id);
        setShowToBottom(false);

        if (messages[historyItem.id]) {
            return;
        }

        setIsLoadingConversation(true);
        try {
            const conversationMessages = await agentService.getMessages(historyItem.id);
            if (conversationMessages.length) {
                const newMessages = conversationMessages.map(msg => {
                    if (msg.event_type === EVENT_TYPES.SYSTEM_MESSAGE_COMPLETED) {
                        return { ...msg.json_content, event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA }
                    }
                    if (msg.event_type === EVENT_TYPES.TOOL_SUMMARY_COMPLETED) {
                        return { ...msg.json_content, event_type: EVENT_TYPES.TOOL_SUMMARY_DELTA }
                    }
                    return { ...msg.json_content, event_type: msg.event_type }
                }) as any[];

                setMessages(prevMessages => ({ ...prevMessages, [historyItem.id]: newMessages }));
            }
        } catch (error) {
            console.error('Failed to load conversation messages:', error);
        } finally {
            setIsLoadingConversation(false);
        }
    };

    const handleNewChat = () => {
        cancelCurrentRequest()
        setCurrentConversationId('temp');
        setShowToBottom(false);
    };

    // Dashboard Actions
    const handleDashboardHistoryClick = (item: AgentConversation) => {
        dashboard.setCurrentConversationId(item.id);
        dashboard.getMessages(item.id);
        setShowToBottom(false);
    };

    const handleDashboardNewChat = () => {
        dashboard.cancelCurrentRequest();
        dashboard.setCurrentConversationId('temp');
        dashboard.setMessages([]);
        setShowToBottom(false);
    };

    const handleDashboardSearch = async (val: string, files: any[], context?: ContextItem[]) => {
        const payload = context?.[0]?.payload || {};
        await generateDashboard(val, payload);
    };

    // Unified Handlers
    const onHistoryItemClick = useDashboardState ? handleDashboardHistoryClick : handleHistoryItemClick;
    const onNewChat = useDashboardState ? handleDashboardNewChat : handleNewChat;
    const onSearch = useDashboardState ? handleDashboardSearch : handleSearch;
    const onStopSearch = useDashboardState ? dashboard.cancelCurrentRequest : cancelCurrentRequest;

    // Scroll Logic
    const scrollToBottom = () => {
        if (activeScrollRef.current) {
            activeScrollRef.current.scrollTo({
                top: activeScrollRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    const handleScroll = () => {
        if (activeScrollRef.current) {
            const {
                scrollTop, scrollHeight, clientHeight 
            } = activeScrollRef.current;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            setShowToBottom(distanceFromBottom > 100);
        }
    };

    const removeContextItem = (index: number) => {
        setContext(context?.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const scrollElement = activeScrollRef.current;
        if (scrollElement) {
            scrollElement.addEventListener('scroll', handleScroll);
            return () => {
                scrollElement.removeEventListener('scroll', handleScroll);
            };
        }
    }, [activeScrollRef.current]);

    const dashboardSuggestions = [
        "Summarize what happened recently",
        "What changed in my data?",
        "What should I pay attention to?",
    ];

    const pulseSuggestions = [
        "Summarize the key insights from this Pulse",
        "What are the significant changes today?",
        "Analyze the performance trends",
    ];

    const editModeSuggestions = [
        { label: "Add a Sales Trend chart", icon: <Icon name="dashboard" /> },
        { label: "Add Top Products chart", icon: <Icon name="dashboard" /> },
        { label: "Add Average Order Value metric", icon: <Icon name="dashboard" /> },
        { label: "Add Customer Retention chart", icon: <Icon name="dashboard" /> },
    ];

    const isPulse = location.pathname.startsWith('/pulse');
    
    let customPromptSuggestions;
    if (isEditMode) {
        customPromptSuggestions = editModeSuggestions;
    } else {
        customPromptSuggestions = isPulse ? pulseSuggestions : dashboardSuggestions;
    }

    return (
        <ChatSidebarUI
            className={className}
            context={context}
            removeContextItem={removeContextItem}
            messages={activeMessages}
            isLoadingConversation={activeIsLoadingConversation}
            isLoadingHistory={activeIsLoadingHistory}
            history={activeHistory}
            inputValue={inputValue}
            isGenerating={activeIsGenerating}
            showToBottom={showToBottom}
            profile={profile}
            onClose={onClose}
            onNewChat={isEditMode ? undefined: onNewChat}
            onHistoryItemClick={isEditMode ? undefined : onHistoryItemClick}
            onSearch={onSearch}
            onStopSearch={onStopSearch}
            onInputChange={setInputValue}
            onScrollToBottom={scrollToBottom}
            onPromptClick={setInputValue}
            chatSideBarScrollRef={activeScrollRef}
            onScroll={handleScroll}
            customPromptSuggestions={customPromptSuggestions}
            inputRef={inputRef as React.RefObject<HTMLTextAreaElement>}
            isEditMode={isEditMode}
        />
    );
};


